import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// material-ui
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

// project imports
import AnswerFeedback from './answerFeedback';
import CitationChips from './citationChips';
import JivanFace from './jivanFace';

const isQuestion = (message) => message.role === 'USER';

// the thread sits on a faint field of the primary colour; the white header and composer
// read as lids over it, so no dividers are needed
export const threadField = (theme) => alpha(theme.palette.primary.light, 0.5);

// questions are filled so they stand off the field
const questionStyle = (theme) => ({
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
});

// answers are white cards on the field
export const answerStyle = (theme) => ({
    bgcolor: theme.palette.background.paper,
    boxShadow: `0 1px 2px ${alpha(theme.palette.primary[800], 0.08)}`
});

const bubbleStyle = (theme, question) => ({
    alignSelf: question ? 'flex-end' : 'flex-start',
    maxWidth: '90%',
    px: 1.5,
    py: 1,
    borderRadius: 2,
    ...(question ? questionStyle(theme) : answerStyle(theme))
});

const Message = ({ message }) => (
    <Box
        data-testid={isQuestion(message) ? 'ask-jivan-question' : 'ask-jivan-answer'}
        sx={(theme) => bubbleStyle(theme, isQuestion(message))}
    >
        {/* body2 carries its own grey in the theme; the bubble decides the text colour */}
        <Typography variant="body2" color="inherit" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {message.content}
        </Typography>
        {!isQuestion(message) && (
            <>
                <CitationChips citations={message.citations} />
                <AnswerFeedback messageId={message.id} rating={message.rating} />
            </>
        )}
    </Box>
);

Message.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        role: PropTypes.oneOf(['USER', 'ASSISTANT']).isRequired,
        content: PropTypes.string.isRequired,
        citations: PropTypes.array,
        rating: PropTypes.oneOf(['UP', 'DOWN'])
    }).isRequired
};

const MessageList = ({ messages, pending }) => {
    const endRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'end' });
    }, [messages.length, pending]);

    return (
        <Stack gap={1.5} sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: threadField }} data-testid="ask-jivan-messages">
            {messages.length === 0 && (
                // Jivan opens the conversation: the hint reads as his first answer
                <Box sx={(theme) => bubbleStyle(theme, false)}>
                    <Typography variant="body2">
                        Ask about a clinical guideline or a workflow. Jivan answers only from the document library and shows where each
                        answer came from.
                    </Typography>
                </Box>
            )}
            {messages.map((message) => (
                <Message key={message.id} message={message} />
            ))}
            {pending && (
                <Stack direction="row" alignItems="center" gap={1} data-testid="ask-jivan-thinking">
                    <CircularProgress size={14} color="primary" />
                    <Typography variant="caption" color="textSecondary">
                        Jivan is thinking…
                    </Typography>
                </Stack>
            )}
            {/* the Easter egg: Jivan waits at the bottom of the thread */}
            <Stack alignItems="center" sx={{ mt: 'auto', pt: 2, opacity: 0.85 }} ref={endRef}>
                <JivanFace size={48} />
            </Stack>
        </Stack>
    );
};

MessageList.propTypes = {
    messages: PropTypes.array.isRequired,
    pending: PropTypes.bool.isRequired
};

export default MessageList;
